(() => {
  // Ensure script runs only once even if Shopify Theme Editor re-renders.
  if (window.ChocowildEditorialInitialized) return;
  window.ChocowildEditorialInitialized = true;

  // Auto-wrap RTE tables and embeds for responsive scrolling without layout overflow
  const formatRteElements = (container = document) => {
    // Wrap tables in responsive scroll wrapper if not already wrapped
    container.querySelectorAll(".rte table").forEach((table) => {
      if (!table.parentElement.classList.contains("cw-table-wrapper")) {
        const wrapper = document.createElement("div");
        wrapper.className = "cw-table-wrapper";
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      }
    });

    // Wrap unwrapped iframes in responsive video/embed wrapper
    container.querySelectorAll(".rte iframe:not(.cw-embed-wrapper iframe)").forEach((iframe) => {
      if (!iframe.parentElement.classList.contains("cw-embed-wrapper")) {
        const wrapper = document.createElement("div");
        wrapper.className = "cw-embed-wrapper";
        iframe.parentNode.insertBefore(wrapper, iframe);
        wrapper.appendChild(iframe);
      }
    });
  };

  const slugifyHeading = (text, index) => {
    const base = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 52);
    return `article-${base || 'section'}-${index + 1}`;
  };

  const initArticleEnhancements = (container = document) => {
    container.querySelectorAll('[data-cw-article-toc]').forEach((toc) => {
      const article = toc.closest('.cw-article');
      const list = toc.querySelector('[data-cw-article-toc-list]');
      if (!article || !list || toc.dataset.cwTocReady) return;

      const headings = [...article.querySelectorAll('.cw-article__body h2')];
      if (!headings.length) return;

      list.replaceChildren();
      headings.forEach((heading, index) => {
        heading.id = heading.id || slugifyHeading(heading.textContent, index);
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.append(link);
        list.append(item);
      });
      toc.hidden = false;
      toc.dataset.cwTocReady = 'true';
    });

    container.querySelectorAll('[data-cw-recently-viewed]').forEach((section) => {
      if (section.dataset.cwRecentReady) return;
      const list = section.querySelector('[data-cw-recent-list]');
      const currentUrl = section.dataset.cwArticleUrl;
      if (!list || !currentUrl) return;

      let recent = [];
      try {
        recent = JSON.parse(window.localStorage.getItem('cw-recent-articles') || '[]');
      } catch (_error) {
        recent = [];
      }

      const previous = recent.filter((item) => item.url && item.url !== currentUrl).slice(0, 3);
      if (previous.length) {
        list.replaceChildren();
        previous.forEach((item) => {
          const card = document.createElement('article');
          card.className = 'cw-journal-card';
          card.dataset.cwJournalReveal = 'true';
          const link = document.createElement('a');
          link.href = item.url;
          const media = document.createElement('div');
          media.className = 'cw-journal-card__media';
          if (item.image) {
            const image = document.createElement('img');
            image.src = item.image;
            image.alt = item.title;
            image.loading = 'lazy';
            media.append(image);
          }
          const title = document.createElement('h3');
          title.textContent = item.title;
          link.append(media, title);
          card.append(link);
          list.append(card);
        });
        section.hidden = false;
      }

      recent = [
        {
          url: currentUrl,
          title: section.dataset.cwArticleTitle || document.title,
          image: section.dataset.cwArticleImage || '',
        },
        ...recent.filter((item) => item.url && item.url !== currentUrl),
      ].slice(0, 6);
      try {
        window.localStorage.setItem('cw-recent-articles', JSON.stringify(recent));
      } catch (_error) {
        // Private browsing or blocked storage: the rest of the article remains usable.
      }
      section.dataset.cwRecentReady = 'true';
    });
  };

  const initJournalCardReveals = (container = document) => {
    const cards = [...(container.querySelectorAll?.('[data-cw-journal-reveal]') || [])].filter(
      (card) => !card.dataset.cwJournalRevealReady
    );
    if (!cards.length) return;

    cards.forEach((card, index) => {
      card.dataset.cwJournalRevealReady = 'true';
      card.style.setProperty('--cw-journal-reveal-index', String(index % 4));
    });

    let observer = null;
    const revealCard = (card) => {
      card.classList.add('is-cw-journal-revealed');
      observer?.unobserve(card);
    };

    if (
      document.body?.dataset.cwMotion === 'false' ||
      document.body?.dataset.cwRevealEnabled === 'false' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !('IntersectionObserver' in window)
    ) {
      cards.forEach(revealCard);
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) revealCard(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );

    cards.forEach((card) => observer.observe(card));
  };

  const initFallingPetals = (container = document) => {
    const fields = [...(container.querySelectorAll?.('[data-cw-petals]') || [])].filter(
      (field) => !field.dataset.cwPetalsReady
    );
    if (!fields.length) return;

    const reducedMotion =
      document.body?.dataset.cwMotion === 'false' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    fields.forEach((field, fieldIndex) => {
      const isMobile = window.matchMedia('(max-width: 800px)').matches;
      const count = isMobile ? 9 : 15;
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < count; index += 1) {
        const petal = document.createElement('span');
        const seed = (index + 1) * 17 + fieldIndex * 13;
        const scale = 0.48 + ((seed * 7) % 54) / 100;
        const x = (seed * 29) % 108 - 4;
        const drift = ((seed * 11) % 32) - 16;
        const duration = 13 + ((seed * 5) % 90) / 10;
        const delay = -((seed * 3) % Math.round(duration * 10)) / 10;
        const opacity = 0.42 + ((seed * 2) % 34) / 100;
        const rotateStart = ((seed * 9) % 80) - 40;

        petal.className = 'cw-editorial-hero__petal';
        petal.style.setProperty('--cw-petal-x', `${x}%`);
        petal.style.setProperty('--cw-petal-scale', String(scale));
        petal.style.setProperty('--cw-petal-drift', `${drift}vw`);
        petal.style.setProperty('--cw-petal-duration', `${duration}s`);
        petal.style.setProperty('--cw-petal-delay', `${delay}s`);
        petal.style.setProperty('--cw-petal-opacity', String(opacity));
        petal.style.setProperty('--cw-petal-rotate-start', `${rotateStart}deg`);
        fragment.append(petal);
      }

      field.append(fragment);
      field.dataset.cwPetalsReady = 'true';
    });
  };

  // Run on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      formatRteElements();
      initArticleEnhancements();
      initJournalCardReveals();
      initFallingPetals();
    });
  } else {
    formatRteElements();
    initArticleEnhancements();
    initJournalCardReveals();
    initFallingPetals();
  }

  // Shopify section render event support
  document.addEventListener("shopify:section:load", (event) => {
    formatRteElements(event.target);
    initArticleEnhancements(event.target);
    initJournalCardReveals(event.target);
    initFallingPetals(event.target);
  });

  // Keep only one FAQ item open within the current container
  document.addEventListener("click", (event) => {
    const summary = event.target.closest(".cw-help summary");
    if (!summary) return;
    const currentDetails = summary.parentElement;
    if (!currentDetails) return;
    const container = currentDetails.parentElement;
    if (!container) return;

    container.querySelectorAll("details[open]").forEach((item) => {
      if (item !== currentDetails) {
        item.removeAttribute("open");
      }
    });
  });
})();
